import { publish } from 'src/components/restructure/product/productEvents'
import classNames from 'classnames'
import { useProductContextSelector } from 'src/components/restructure/product/contexts'
import { makeAddToCartEvent, sendEvent } from 'src/utils/restructure/analytics'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import DktLoader from 'src/components/DktLoader/DktLoader'
import { useState } from 'react'
import { navigate } from 'gatsby'
import { useCartFeedback } from 'src/components/contexts/CartFeedbackContext'
import ModalBlockAddToCart from 'src/components/common/Modal/ModalBlockAddToCart'
import { priceRangeCoversBike } from 'src/utils/insurance'
import { isValidCEPSize } from 'src/components/checkout/steps/utils'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { ShoppingAddToBag } from 'src/components/Icons/ShoppingAddToBagIcon'

import storeConfig from '../../../../../../../../store.config'
import { useBuyButton } from './hooks/useBuyButton'
import { InsuranceBottomSheet } from './InsuranceBottomSheet/InsuranceBottomSheet'
import { InsuranceSuccessModal } from './InsuranceBottomSheet/InsuranceSuccessModal'
import ModalLearnMoreBikeInsurance from '../../LearnMoreBikeInsurance/ModalLearnMoreBikeInsurance/ModalLearnMoreBikeInsurance'
import { useBikeInsuranceCheck } from './hooks/useBikeInsuranceCheck'
import { fetchProductInsurance } from './hooks/usefetchProductInsurance'
import { useCrossSellCardContext } from '../../CrossSellCard/cross-sell-card-context'

interface BuyButtonProps {
  quantity: number
  isVisible?: boolean
  type?: 'addToCart' | 'buyNow'
  loaderVariant?: 'dkt-blue' | 'black' | 'white'
  cmsData?: Record<string, unknown>
  visualMode?: 'default' | 'icon'
  className?: string
}

export function BuyButton({
  quantity,
  isVisible = true,
  type = 'addToCart',
  loaderVariant,
  cmsData,
  visualMode = 'default',
  className = '',
}: Readonly<BuyButtonProps>) {
  const [showInsuranceSheet, setShowInsuranceSheet] = useState(false)
  const [showLearnMore, setShowLearnMore] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [actionType, setActionType] = useState<'addToCart' | 'buyNow'>(
    'addToCart'
  )

  const { pageData } = useGlobalContext()

  const lastClickedList =
    typeof window !== 'undefined'
      ? localStorage?.getItem?.('lastClickedList')
      : ''

  const eventData = {
    'product list type': pageData.previousPageType,
    'product list name': lastClickedList ?? '',
  }

  const {
    crossSellCartItem,
    includeCrossSellProductToCart,
    openSkuSelectionModal,
  } = useCrossSellCardContext()

  const { openCartFeedback } = useCartFeedback()
  const {
    orderForm: { items, messages, shippingData },
    isLoading,
  } = useCheckout()

  const product = useProductContextSelector((ctx) => ctx.product)
  const currentSku = useProductContextSelector((ctx) => ctx.currentSku)
  const deliveryIsAvailable = useProductContextSelector(
    (ctx) => ctx.deliveryIsAvailable
  )

  const isPreSale = useProductContextSelector((ctx) => ctx.isPreSale)
  const freightSimulationValue = useProductContextSelector(
    (ctx) => ctx.freightSimulationValue
  )

  const {
    addToCart,
    openModal,
    openPreSaleModal,
    setOpenPreSaleModal,
    setOpenModal,
    handleClearCartAndAdd,
    validateGiftCardRestrictions,
  } = useBuyButton()

  const { checkExistingInsuranceInRange } = useBikeInsuranceCheck()
  const { isAvailable, itemId, sellerDefault, ean } = currentSku

  const qttyAlreadyInCart = items?.find((item) => item.id === itemId)?.quantity

  const availableQtty = sellerDefault?.sellerOffer?.quantity

  const quantityNotAvailableMessage = messages?.find(
    (message) => message.code === 'itemQuantityNotAvailable'
  )

  const cannotIncreaseQuantity =
    quantityNotAvailableMessage?.fields?.ean === ean

  const shouldPostalCodeCheckApply =
    (shippingData?.address?.postalCode ||
      shippingData?.address?.postalCode !== '') &&
    isValidCEPSize(shippingData?.address?.postalCode)

  const shouldDisable =
    !isVisible ||
    isAvailable === false ||
    (deliveryIsAvailable === false &&
      shouldPostalCodeCheckApply &&
      freightSimulationValue !== null) ||
    (qttyAlreadyInCart ?? 0) >= availableQtty ||
    cannotIncreaseQuantity

  const isBike = product?.categories?.find(
    (cat: string) => cat === '/Equipamentos/Bicicletas/'
  )

  const isSoldByDecathlon = product?.sellerInfo?.id === '1'

  const skuBikePrice = sellerDefault?.sellerOffer?.price

  const handleBuyButton = async () => {
    publish('addToCart')

    const item = {
      id: itemId,
      productId: product.productId,
      isPreSale,
      quantity: qttyAlreadyInCart ? qttyAlreadyInCart + quantity : quantity,
      seller: sellerDefault.sellerId,
      index: 0,
      price: sellerDefault.sellerOffer.price,
      amplitude: currentSku.analytics?.amplitude,
    }

    const crossSellHasMultipleSkus =
      includeCrossSellProductToCart && !crossSellCartItem

    const itemsToAdd = crossSellCartItem ? [item, crossSellCartItem] : item
    const effectiveType = crossSellHasMultipleSkus ? 'addToCart' : type
    const shouldOpenFeedback = !crossSellHasMultipleSkus

    if (isBike && isSoldByDecathlon) {
      // PRIMEIRO: Verifica restrição de Gift Card ANTES de adicionar
      const hasGiftCardRestriction = validateGiftCardRestrictions(item, type)

      if (hasGiftCardRestriction) {
        // Se tem gift card no carrinho, apenas abre o modal e para aqui
        // Não adiciona ao carrinho nem abre modal de insurance ainda
        return
      }

      const hasExistingInsurance = await checkExistingInsuranceInRange(
        skuBikePrice,
        itemId
      )

      if (hasExistingInsurance) {
        await addToCart({
          item: itemsToAdd,
          type: effectiveType,
          shouldOpenFeedback,
          eventData,
        })

        return
      }

      // Verifica se há seguros disponíveis para o preço da bike
      try {
        const insuranceData = await fetchProductInsurance({
          productIds: storeConfig.InsuranceID,
        })

        const insuranceItems = insuranceData
          ?.flatMap((insuranceProduct) => insuranceProduct?.items ?? [])
          .filter(
            (insurance: { referenceId?: string }) =>
              insurance?.referenceId &&
              priceRangeCoversBike(insurance.referenceId, skuBikePrice)
          )

        // Se não houver seguros disponíveis, adiciona ao carrinho sem abrir o modal
        if (!insuranceItems || insuranceItems?.length === 0) {
          await addToCart({
            item: itemsToAdd,
            type: effectiveType,
            shouldOpenFeedback,
            eventData,
          })

          return
        }
      } catch (error) {
        console.error('Erro ao verificar seguros disponíveis:', error)
        // Em caso de erro, adiciona ao carrinho sem abrir o modal
        await addToCart({
          item: itemsToAdd,
          type: effectiveType,
          shouldOpenFeedback,
          eventData,
        })

        return
      }

      setActionType(type)
      // Sempre usar 'addToCart' aqui para não redirecionar ao checkout antes do modal
      await addToCart({
        item: itemsToAdd,
        type: 'addToCart',
        shouldOpenFeedback: false,
        eventData,
      })
      setShowInsuranceSheet(true)
    } else {
      await addToCart({
        item: itemsToAdd,
        type: effectiveType,
        shouldOpenFeedback,
        eventData,
      })
    }

    const addToCartEvent = makeAddToCartEvent([
      { product, productItem: currentSku },
    ])

    sendEvent(addToCartEvent)

    if (crossSellHasMultipleSkus) {
      openSkuSelectionModal(itemId)
    }
  }

  const handleInsuranceClose = () => {
    setShowInsuranceSheet(false)

    if (actionType === 'buyNow') {
      // Se for "Comprar agora" e clicou em "Não quero", redireciona para checkout
      navigate('/checkout/profile')
    } else {
      // Se for "Adicionar ao carrinho", abre o minicart

      openCartFeedback([itemId])
    }
  }

  const handleInsuranceSuccess = () => {
    setShowInsuranceSheet(false)
    setShowSuccessModal(true)
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)

    if (actionType === 'buyNow') {
      // Se for "Comprar agora" e clicou em "Entendi", redireciona para checkout
      navigate('/checkout/profile')
    } else {
      // Se for "Adicionar ao carrinho", abre o minicart
      openCartFeedback()
    }
  }

  // Função específica para bikes: limpa carrinho, adiciona bike e abre modal de insurance
  const handleClearCartAndAddBike = async () => {
    // Chama handleClearCartAndAdd com callback para abrir o insurance modal
    await handleClearCartAndAdd(() => {
      setShowInsuranceSheet(true)
    })
  }

  const handleClearCartAndAddCurrentProduct = async () => {
    await handleClearCartAndAdd(async () => {
      await handleBuyButton()
      setOpenPreSaleModal(false)
    })

    if (type === 'buyNow') {
      navigate('/checkout/profile')
    }
  }

  const buttonLabel =
    type === 'addToCart' ? 'Adicionar ao carrinho' : 'Comprar agora'

  const buttonDisabledClasses =
    'cursor-not-allowed border-restructure-border-disabled bg-restructure-background-disabled text-restructure-disabled hover:border-restructure-border-disabled hover:text-restructure-disabled'

  const isIconMode = visualMode === 'icon'

  const buttonBaseClasses = isIconMode
    ? 'w-16 px-0 py-0 min-h-16 flex items-center justify-center bg-transparent border border-shippingCampaignDivider'
    : 'flex-1 w-full px-xxs py-5 min-h-16 rounded-round font-medium text-base'

  const iconModeActionClasses =
    'bg-restructure-primary-inverted hover:bg-restructure-primary-inverted'

  const addToCartActionClasses =
    'bg-restructure-background-neutral-3 text-primaryBlue'

  const buyNowActionClasses =
    'bg-restructure-background-action-1 hover:bg-restructure-background-action-1-hover text-restructure-primary-inverted'

  const buttonActionClasses = isIconMode
    ? iconModeActionClasses
    : type === 'addToCart'
    ? addToCartActionClasses
    : buyNowActionClasses

  const loaderWidth = isIconMode ? 16 : 20
  const resolvedLoaderVariant = isIconMode ? 'black' : loaderVariant

  const renderButtonContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center w-full">
          <DktLoader width={loaderWidth} variant={resolvedLoaderVariant} />
        </div>
      )
    }

    if (isIconMode) {
      return (
        <>
          <ShoppingAddToBag color="#101010" width={24} height={24} />
          <span className="sr-only">{buttonLabel}</span>
        </>
      )
    }

    return buttonLabel
  }

  const iconModeAriaLabel = isIconMode ? buttonLabel : undefined

  if (isBike) {
    return (
      <>
        <button
          data-testid={`${buttonLabel
            .toLowerCase()
            .replace(/\s+/g, '-')}-buy-button`}
          aria-label={iconModeAriaLabel}
          disabled={shouldDisable}
          className={classNames(
            buttonBaseClasses,
            buttonActionClasses,
            shouldDisable && buttonDisabledClasses,
            className
          )}
          onClick={handleBuyButton}
        >
          {renderButtonContent()}
        </button>
        <InsuranceBottomSheet
          isOpen={showInsuranceSheet}
          onClose={handleInsuranceClose}
          onSuccess={handleInsuranceSuccess}
          onOpenLearnMore={() => {
            setShowInsuranceSheet(false)
            setShowLearnMore(true)
          }}
        />
        <InsuranceSuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
        />
        <ModalLearnMoreBikeInsurance
          isOpen={showLearnMore}
          onDismiss={() => {
            setShowLearnMore(false)
            setTimeout(() => setShowInsuranceSheet(true), 200)
          }}
          cmsData={cmsData}
        />
        {/* Caso tenha um "Cartão Presente" no carrinho ao adicionar uma "Bicicleta", exiba o modal */}
        <ModalBlockAddToCart
          openModal={openModal}
          setOpenModal={setOpenModal}
          mainMessage="Não é possível comprar um Cartão Presente junto com outros produtos."
          subMessage="Deseja limpar seu carrinho para adicionar este produto ao carrinho?"
          buttonLabel="Limpar e adicionar ao carrinho"
          cancelLabel="Cancelar"
          onClearCart={handleClearCartAndAddBike}
        />
      </>
    )
  }

  return (
    <>
      <button
        data-testid={`${buttonLabel
          .toLowerCase()
          .replace(/\s+/g, '-')}-buy-button`}
        aria-label={iconModeAriaLabel}
        disabled={shouldDisable}
        className={classNames(
          buttonBaseClasses,
          buttonActionClasses,
          shouldDisable && buttonDisabledClasses,
          className
        )}
        onClick={handleBuyButton}
      >
        {renderButtonContent()}
      </button>
      {/* Caso tenha um "Cartão Presente" no carrinho ao adicionar um "Produto diferente", exiba o modal */}
      <ModalBlockAddToCart
        openModal={openModal}
        setOpenModal={setOpenModal}
        mainMessage="Não é possível comprar um Cartão Presente junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para adicionar este produto ao carrinho?"
        buttonLabel="Limpar e adicionar ao carrinho"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndAdd}
      />
      <ModalBlockAddToCart
        openModal={openPreSaleModal}
        setOpenModal={setOpenPreSaleModal}
        mainMessage="Não é possível comprar um produto em pré venda junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para continuar com a compra?"
        buttonLabel="Limpar carrinho e continuar"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndAddCurrentProduct}
      />
    </>
  )
}
